import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Sparkles, Lightbulb, PhoneCall } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useI18n } from "@/i18n";
import { BookingDialog } from "@/components/BookingDialog";

const PROFILE_IMG = {
  energie: "/img/profile-energie.jpg",
  poids: "/img/profile-poids.jpg",
  mobilite: "/img/profile-mobilite.jpg",
  performance: "/img/profile-performance.jpg",
  longevite: "/img/profile-longevite.jpg",
};

export function ResultScreen({ result }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [bookingOpen, setBookingOpen] = useState(false);
  const img = PROFILE_IMG[result.profile_primary] || PROFILE_IMG.energie;
  const advice = Array.isArray(result.advice) ? result.advice : [];
  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="max-w-3xl w-full mx-auto px-6 py-6">
        <Logo imgClassName="h-11" />
      </header>
      <motion.main
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl w-full mx-auto px-6 pb-20"
        data-testid="result-screen"
      >
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d7de3b] text-[#1a2d56] text-sm font-bold">
          <Sparkles size={15} /> {t("res_badge")}
        </div>

        <div className="relative mt-6 rounded-3xl overflow-hidden h-60 sm:h-72">
          <img src={img} alt={result.profile_primary_name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a2d56]/85 via-[#1a2d56]/25 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5">
            <p className="text-[#d7de3b] text-sm font-bold uppercase tracking-wider">{t("res_main_prefix")}</p>
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold text-white leading-tight" data-testid="result-profile-name">
              {result.profile_primary_name}
            </h1>
            {result.profile_secondary_name && (
              <p className="mt-1 text-white/85 text-sm font-medium">
                {t("res_secondary_prefix")} {result.profile_secondary_name}
              </p>
            )}
          </div>
        </div>

        <p className="mt-7 text-lg text-[#4b5b7c] leading-relaxed">{result.lead_description}</p>

        {advice.length > 0 && (
          <div className="mt-8 rounded-2xl bg-white border border-[#e2e8f0] p-6" data-testid="result-advice">
            <h2 className="font-head font-bold text-[#1a2d56] flex items-center gap-2 mb-4">
              <Lightbulb size={18} className="text-[#d7de3b]" /> {t("res_advice_title")}
            </h2>
            <ul className="space-y-3">
              {advice.map((a, i) => (
                <li key={a} className="flex gap-3" data-testid={`advice-item-${i}`}>
                  <span className="shrink-0 w-6 h-6 rounded-full bg-[#1a2d56] text-[#d7de3b] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-[#4b5b7c] leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 rounded-2xl bg-[#1a2d56] text-white p-6">
          <span className="text-sm font-semibold text-[#d7de3b] uppercase tracking-wider">{t("res_firststep_label")}</span>
          <p className="font-head text-xl font-bold mt-1">{result.first_step}</p>
        </div>

        <div className="mt-6 flex gap-3 rounded-2xl bg-[#d7de3b]/20 border border-[#d7de3b] p-5" data-testid="result-team-contact">
          <PhoneCall className="text-[#1a2d56] shrink-0 mt-0.5" size={22} />
          <div>
            <p className="font-head font-bold text-[#1a2d56]">{t("res_team_contact_title")}</p>
            <p className="text-sm text-[#4b5b7c] mt-1 leading-relaxed">{t("res_team_contact")}</p>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => setBookingOpen(true)}
            data-testid="result-cta-rdv"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#d7de3b] text-[#1a2d56] font-head font-bold rounded-full px-8 py-4 hover:-translate-y-0.5 transition-transform"
          >
            {t("res_cta_rdv")}
          </button>
          <button
            onClick={() => navigate("/")}
            data-testid="result-home-button"
            className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#1a2d56] text-[#1a2d56] font-head font-bold rounded-full px-8 py-4 hover:bg-[#1a2d56] hover:text-white transition-colors"
          >
            {t("res_home")}
          </button>
        </div>

        {result.safety_warning && (
          <div className="mt-6 flex gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4">
            <AlertTriangle className="text-amber-500 shrink-0" size={20} />
            <p className="text-sm text-amber-800">{t("res_safety")}</p>
          </div>
        )}

        <p className="mt-6 text-sm text-[#9aa6c0] leading-relaxed">{t("res_disclaimer")}</p>
      </motion.main>
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} source="Résultat test" />
    </div>
  );
}
